import { Buffer, getGlobalObject, makeUUID4, nextMonth } from '@stacks/common';
import { decryptECIES, encryptECIES, makeECPrivateKey, publicKeyToAddress, } from '@stacks/encryption';
import { SECP256K1Client, TokenSigner } from 'jsontokens';
import { DEFAULT_SCOPE } from './constants';
import { makeDIDFromAddress } from './dids';
const VERSION = '1.4.0';
export function generateTransitKey() {
    const transitKey = makeECPrivateKey();
    return transitKey;
}
export function makeAuthRequest(transitPrivateKey, redirectURI, manifestURI, scopes = DEFAULT_SCOPE.slice(), appDomain, expiresAt = nextMonth().getTime(), extraParams = {}) {
    const getWindowOrigin = (paramName) => {
        const location = getGlobalObject('location', {
            throwIfUnavailable: true,
            usageDesc: `makeAuthRequest([${paramName}=undefined])`,
        });
        return location === null || location === void 0 ? void 0 : location.origin;
    };
    if (!redirectURI) {
        redirectURI = `${getWindowOrigin('redirectURI')}/`;
    }
    if (!manifestURI) {
        manifestURI = `${getWindowOrigin('manifestURI')}/manifest.json`;
    }
    if (!appDomain) {
        appDomain = getWindowOrigin('appDomain');
    }
    const payload = Object.assign({}, extraParams, {
        jti: makeUUID4(),
        iat: Math.floor(new Date().getTime() / 1000),
        exp: Math.floor(expiresAt / 1000),
        iss: null,
        public_keys: [],
        domain_name: appDomain,
        manifest_uri: manifestURI,
        redirect_uri: redirectURI,
        version: VERSION,
        do_not_include_profile: true,
        supports_hub_url: true,
        scopes,
    });
    const publicKey = SECP256K1Client.derivePublicKey(transitPrivateKey);
    payload.public_keys = [publicKey];
    const address = publicKeyToAddress(publicKey);
    payload.iss = makeDIDFromAddress(address);
    const tokenSigner = new TokenSigner('ES256k', transitPrivateKey);
    const token = tokenSigner.sign(payload);
    return token;
}
export async function encryptPrivateKey(publicKey, privateKey) {
    const encryptedObj = await encryptECIES(publicKey, Buffer.from(privateKey), true);
    const encryptedJSON = JSON.stringify(encryptedObj);
    return Buffer.from(encryptedJSON).toString('hex');
}
export async function decryptPrivateKey(privateKey, hexedEncrypted) {
    const unhexedString = Buffer.from(hexedEncrypted, 'hex').toString();
    const encryptedObj = JSON.parse(unhexedString);
    const decrypted = await decryptECIES(privateKey, encryptedObj);
    if (typeof decrypted !== 'string') {
        throw new Error('Unable to correctly decrypt private key');
    }
    else {
        return decrypted;
    }
}
export async function makeAuthResponse(privateKey, profile = {}, metadata, coreToken = null, appPrivateKey = null, expiresAt = nextMonth().getTime(), transitPublicKey = null, hubUrl = null, blockstackAPIUrl = null, associationToken = null, appPrivateKeyFromWalletSalt = null) {
    const publicKey = SECP256K1Client.derivePublicKey(privateKey);
    const address = publicKeyToAddress(publicKey);
    let privateKeyPayload = appPrivateKey;
    let coreTokenPayload = coreToken;
    let additionalProperties = {};
    if (appPrivateKey !== undefined && appPrivateKey !== null) {
        if (transitPublicKey !== undefined && transitPublicKey !== null) {
            privateKeyPayload = await encryptPrivateKey(transitPublicKey, appPrivateKey);
            if (coreToken !== undefined && coreToken !== null) {
                coreTokenPayload = await encryptPrivateKey(transitPublicKey, coreToken);
            }
        }
        additionalProperties = {
            email: (metadata === null || metadata === void 0 ? void 0 : metadata.email) ? metadata.email : null,
            profile_url: (metadata === null || metadata === void 0 ? void 0 : metadata.profileUrl) ? metadata.profileUrl : null,
            hubUrl,
            blockstackAPIUrl,
            associationToken,
            version: VERSION,
        };
    }
    else {
    }
    const payload = Object.assign({}, {
        jti: makeUUID4(),
        iat: Math.floor(new Date().getTime() / 1000),
        exp: Math.floor(expiresAt / 1000),
        iss: makeDIDFromAddress(address),
        private_key: privateKeyPayload,
        public_keys: [publicKey],
        appPrivateKeyFromWalletSalt,
        profile,
        core_token: coreTokenPayload,
    }, additionalProperties);
    const tokenSigner = new TokenSigner('ES256k', privateKey);
    return tokenSigner.sign(payload);
}
//# sourceMappingURL=messages.js.map